import { ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight } from "lucide-react";

interface ProductPaginationProps {
  currentPage: number;
  totalPages: number;
  onPageChange: (page: number) => void;
  scrollTargetId?: string;
}

export const ProductPagination = ({
  currentPage,
  totalPages,
  onPageChange,
  scrollTargetId,
}: ProductPaginationProps) => {
  if (totalPages <= 1) return null;

  const goTo = (page: number) => {
    onPageChange(page);
    if (scrollTargetId) {
      document.getElementById(scrollTargetId)?.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  // Build page numbers to show: always first, last, current ± 1, with ellipsis
  const pages: (number | "ellipsis")[] = [];
  const addPage = (p: number) => {
    if (p >= 0 && p < totalPages && !pages.includes(p)) pages.push(p);
  };

  addPage(0);
  if (currentPage > 2) pages.push("ellipsis");
  addPage(currentPage - 1);
  addPage(currentPage);
  addPage(currentPage + 1);
  if (currentPage < totalPages - 3) pages.push("ellipsis");
  addPage(totalPages - 1);

  // Deduplicate and sort, keeping ellipsis in place
  const finalPages: (number | "ellipsis")[] = [];
  const sorted = [...new Set(pages.filter((p): p is number => typeof p === "number"))].sort((a, b) => a - b);
  
  for (let i = 0; i < sorted.length; i++) {
    if (i > 0 && sorted[i] - sorted[i - 1] > 1) {
      finalPages.push("ellipsis");
    }
    finalPages.push(sorted[i]);
  }

  const btnBase = "flex items-center justify-center rounded-full border transition-colors text-[13px] tabular-nums";
  const btnSize = "h-9 w-9";
  const btnNav = `${btnBase} ${btnSize} border-border hover:border-foreground/30 disabled:opacity-30 disabled:cursor-not-allowed`;
  const btnPage = (active: boolean) =>
    `${btnBase} ${btnSize} ${
      active
        ? "bg-foreground text-primary-foreground border-foreground"
        : "border-border hover:border-foreground/30 hover:bg-muted/50 text-muted-foreground"
    }`;

  return (
    <div className="flex items-center justify-center gap-1.5 mt-10 flex-wrap">
      {/* First */}
      <button onClick={() => goTo(0)} disabled={currentPage === 0} className={btnNav} aria-label="First page">
        <ChevronsLeft className="h-4 w-4" />
      </button>
      {/* Previous */}
      <button onClick={() => goTo(currentPage - 1)} disabled={currentPage === 0} className={btnNav} aria-label="Previous page">
        <ChevronLeft className="h-4 w-4" />
      </button>

      {/* Page numbers */}
      {finalPages.map((item, i) =>
        item === "ellipsis" ? (
          <span key={`e-${i}`} className="px-1 text-muted-foreground text-[13px]">…</span>
        ) : (
          <button key={item} onClick={() => goTo(item)} aria-label={`Go to page ${item + 1}`} aria-current={item === currentPage ? "page" : undefined} className={btnPage(item === currentPage)}>
            {item + 1}
          </button>
        )
      )}

      {/* Next */}
      <button onClick={() => goTo(currentPage + 1)} disabled={currentPage === totalPages - 1} className={btnNav} aria-label="Next page">
        <ChevronRight className="h-4 w-4" />
      </button>
      {/* Last */}
      <button onClick={() => goTo(totalPages - 1)} disabled={currentPage === totalPages - 1} className={btnNav} aria-label="Last page">
        <ChevronsRight className="h-4 w-4" />
      </button>
    </div>
  );
};
